import type { CoachingSession } from '@efdigital/ll-addon-data';
import { queryCoachingSessionsByCoach } from '@efdigital/ll-xceed-v2/data/builders/coaching/coaching-sessions';
import {
  COACH_ACTION_REQUIRED_QUERY_KEYS,
  COACH_PENDING_CONFIRMATION_KEYS,
  filterCoachActionRequired,
} from '@efdigital/ll-xceed-v2/utils/coaching/session-current-categories';
import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';

/**
 * How many sessions are waiting on the coach, for the Sessions nav badge.
 *
 * The coachee side reads this straight off its planner, which holds every
 * session for the plan. A coach has no equivalent - the count is assembled
 * from two queries - so it lives here, where the nav can reach it from
 * `coaching.coach` while the pages that need the sessions themselves stay
 * where they are.
 */
export default class CoachActionRequiredService extends Service {
  @tracked count = 0;

  /**
   * A booking awaiting the coach's confirmation is as much theirs to act on as
   * a flagged or unreported session, so both are counted - the same definition
   * the sessions page badges and the dashboard's "Need your attention" card
   * uses.
   */
  setFromSessions(
    pendingConfirmation: CoachingSession[],
    actionRequired: CoachingSession[]
  ): void {
    this.count = pendingConfirmation.length + actionRequired.length;
  }

  /**
   * Loads the count directly. Used on entering the coach area, so the badge is
   * right even when the coach lands somewhere that does not fetch sessions
   * itself - a bookmark straight to Messages, say. Pages that do fetch them
   * call `setFromSessions` instead and cost nothing.
   */
  async sync(store: Store, coachUserId: string): Promise<void> {
    const [pending, actionRequired] = await Promise.all([
      store.request(
        queryCoachingSessionsByCoach(
          coachUserId,
          COACH_PENDING_CONFIRMATION_KEYS
        )
      ),
      store.request(
        queryCoachingSessionsByCoach(
          coachUserId,
          COACH_ACTION_REQUIRED_QUERY_KEYS
        )
      ),
    ]);

    this.setFromSessions(
      pending.content.data,
      filterCoachActionRequired(actionRequired.content.data)
    );
  }
}

declare module '@ember/service' {
  interface Registry {
    'coach-action-required': CoachActionRequiredService;
  }
}
